<template>
    <div class="update-video-container" v-if="!videoNotFound && !isLoading && isAuthorized">
        <div v-if="canManageOwner" class="admin-owner-panel">
            <div class="admin-owner-header">
                <div>
                    <h3>{{ ta ? 'காணொளி உரிமையாளர்' : 'Video Owner' }}</h3>
                    <p>{{ ta ? 'இந்த காணொளியின் உரிமையாளரை மாற்றவும்.' : 'Change which user owns this video.' }}</p>
                </div>
                <div v-if="selectedOwner" class="selected-owner">
                    <img :src="getUserAvatar(selectedOwner)" :alt="displayUserName(selectedOwner)" @error="handleAvatarError" />
                    <div>
                        <strong>{{ displayUserName(selectedOwner) }}</strong>
                        <span v-if="selectedOwner.username">@{{ selectedOwner.username }}</span>
                    </div>
                </div>
            </div>
            <div class="owner-search-box">
                <vue-feather type="search" size="16"></vue-feather>
                <input
                    v-model="ownerSearchQuery"
                    :placeholder="ta ? 'பயனரைத் தேடுங்கள்...' : 'Search users to assign owner...'"
                    @input="searchOwners"
                    @focus="showOwnerResults = ownerSearchResults.length > 0"
                />
                <vue-feather v-if="ownerSearching" type="loader" animation="spin" size="16"></vue-feather>
            </div>
            <div v-if="showOwnerResults" class="owner-results">
                <button
                    v-for="user in ownerSearchResults"
                    :key="user.id"
                    type="button"
                    class="owner-result"
                    @click="selectOwner(user)"
                >
                    <img :src="getUserAvatar(user)" :alt="displayUserName(user)" @error="handleAvatarError" />
                    <span>
                        <strong>{{ displayUserName(user) }}</strong>
                        <small v-if="user.username">@{{ user.username }}</small>
                    </span>
                    <vue-feather v-if="newVideo.ownerId === user.id" type="check" size="16"></vue-feather>
                </button>
            </div>
        </div>

        <video-form 
            mode="edit"
            :video="newVideo"
            :categories="categories"
            :tags="videoTags"
            :available-tags="availableTags"
            :primary-category="primaryCategory"
            :secondary-category="secondaryCategory"
            :is-updating="creating"
            :is-updated="created"
            :is-deleting="deleting"
            :is-deleted="deleted"
            :error-message="errorMessage"
            :is-uploading-video="uploadingReplacement"
            :is-preparing-video="preparingReplacement"
            :upload-progress="replacementUploadProgress"
            :upload-status="replacementUploadStatus"
            :upload-message="replacementUploadMessage"
            :thumbnail-refresh-key="thumbnailRefreshKey"
            @update:video="updateVideo"
            @update:tags="updateTags"
            @update:primary-category="updatePrimaryCategory"
            @update:secondary-category="updateSecondaryCategory"
            @replace-video="replaceVideoFile"
            @save="saveVideo"
            @delete="deleteVideo"
            @clear-error="errorMessage = ''"
        />
    </div>
    
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner">
            <vue-feather type="loader" animation="spin" size="48"></vue-feather>
        </div>
        <h3>{{ ta ? 'காணொளி விவரங்களை ஏற்றுகிறது...' : 'Loading Video Details...' }}</h3>
        <p>{{ ta ? 'காணொளி தகவலைப் பெறும் வரை காத்திருக்கவும்.' : 'Please wait while we fetch the video information.' }}</p>
    </div>
    
    <!-- Not Found State -->
    <div v-if="videoNotFound && !isLoading" class="not-found">
        <h3>{{ ta ? '404 - காணொளி கிடைக்கவில்லை' : '404 - Video Not Found' }}</h3>
        <p>{{ ta ? 'நீங்கள் தேடும் காணொளி இல்லை அல்லது நீக்கப்பட்டுள்ளது.' : "The video you're looking for doesn't exist or has been removed." }}</p>
        <button @click="$router.push('/')" class="btn btn-primary">{{ ta ? 'முகப்புக்குச் செல்' : 'Go Home' }}</button>
    </div>
    
    <!-- Unauthorized State -->
    <div v-if="!videoNotFound && !isLoading && !isAuthorized" class="unauthorized">
        <div class="unauthorized-icon">
            <vue-feather type="lock" size="64" stroke-width="1"></vue-feather>
        </div>
        <h3>{{ ta ? 'அணுகல் மறுக்கப்பட்டது' : 'Access Denied' }}</h3>
        <p v-if="!isOwner">{{ ta ? 'இந்த காணொளியைத் திருத்த உங்களுக்கு அனுமதி இல்லை.' : "You don't have permission to edit this video." }}</p>
        <p v-else>{{ ta ? 'காணொளிகளைத் திருத்த நீங்கள் உள்நுழைகய வேண்டும்.' : 'You need to be logged in to edit videos.' }}</p>
        <div class="unauthorized-actions">
            <button @click="$router.push('/')" class="btn btn-primary">{{ ta ? 'முகப்புக்குச் செல்' : 'Go Home' }}</button>
            <button @click="$router.push(`/watch/${newVideo.id}`)" class="btn btn-secondary">{{ ta ? 'காணொளியைக் காண்க' : 'View Video' }}</button>
        </div>
    </div>
</template>

<script>
import { API } from 'aws-amplify';
import { Storage } from "@aws-amplify/storage"
import { track as analyticsTrack } from '../utils/analytics';
import { listCategoryModelsWithChildren, getVideoModelWithTags, listTagModelsForAutocomplete, getVideoProcessingStatus, getUserModelLiteByUsername, listUserModelsForMessageSearch } from '../graphql/custom.js';
import { updateVideoModel, deleteVideoModel, createTagModel, createVideoTags, deleteVideoTags } from '../graphql/mutations'
import VideoForm from './VideoForm.vue'
import VueFeather from 'vue-feather';
import { canEditVideo, isAdminUser, isVideoOwner } from '../utils/auth.js';

export default {
    name: 'update-video',
    components: {
        VideoForm,
        VueFeather
    },
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' }
    },
    computed: {
        currentLanguage() {
            return this.languageRef?.value || this.languageRef || 'EN';
        },
        ta() {
            return this.currentLanguage === 'TA';
        },
        canManageOwner() {
            return this.isAdmin === true;
        }
    },
    mounted() {
        this.loadVideoDetails()
    },
    beforeUnmount() {
        if (this.replacementPollTimer) {
            clearInterval(this.replacementPollTimer);
            this.replacementPollTimer = null;
        }
        if (this.ownerSearchTimeout) {
            clearTimeout(this.ownerSearchTimeout);
        }
    },
    data() {
        return {
            categories: [],
            created: false,
            creating: false,
            deleted: false,
            deleting: false,
            currentUser: null,
            primaryCategory: null,
            secondaryCategory: null,
            videoNotFound: false,
            isOwner: false, // Track if current user is video owner
            isAdmin: false,
            isAuthorized: false, // Track if user is authorized to edit
            isLoading: true, // Loading state for video details
            videoTags: [], // Will be populated when we add tag support
            availableTags: [], // For autocomplete
            errorMessage: '', // For displaying error messages
            uploadingReplacement: false,
            preparingReplacement: false,
            replacementUploadProgress: 0,
            replacementUploadStatus: '',
            replacementUploadMessage: '',
            replacementPollTimer: null,
            replacementProcessingReadyHandled: false,
            thumbnailRefreshKey: Date.now(),
            ownerSearchQuery: '',
            ownerSearchResults: [],
            ownerSearchTimeout: null,
            ownerSearching: false,
            showOwnerResults: false,
            selectedOwner: null,
            originalOwnerId: null,
            newVideo: {
                id: '',
                titleTA: '',
                titleEN: '',
                descriptionEN: '',
                descriptionTA: '',
                significantDate: null,
                thumbnailUrl: null,
                ownerId: null,
                publishState: 'pending',
                videoModelPrimaryCategoryId: null,
                videoModelSecondaryCategoryId: null
            },
        }
    },
    methods: {
        async loadVideoDetails() {
            this.isLoading = true;
            this.videoNotFound = false;
            this.isAuthorized = false;
            this.isOwner = false;
            
            try {
                let videoId = this.$route.params.id
                const userInput = {
                    id: videoId
                };
                
                // Load categories, video details, and available tags in parallel
                const [categoryRes, videoRes, tagsRes] = await Promise.all([
                    API.graphql({ query: listCategoryModelsWithChildren }),
                    API.graphql({
                        query: getVideoModelWithTags,
                        variables: userInput,
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    }),
                    API.graphql({
                        query: listTagModelsForAutocomplete,
                        variables: { limit: 100 }
                    })
                ]);
                
                // Set categories
                this.categories = categoryRes.data.listCategoryModels.items;
                
                // Set available tags for autocomplete
                this.availableTags = tagsRes.data.listTagModels.items;
                
                // Check if video exists
                this.videoNotFound = videoRes.data.getVideoModel == null;
                
                if (!this.videoNotFound) {
                    console.log('user: ', userInput, videoRes)
                    const videoData = videoRes.data.getVideoModel;
                    
                    // Check authorization
                    this.isOwner = await isVideoOwner(videoData);
                    this.isAdmin = await isAdminUser();
                    this.isAuthorized = await canEditVideo(videoData);
                    
                    this.newVideo.id = videoData.id
                    this.newVideo.titleTA = videoData.titleTA
                    this.newVideo.titleEN = videoData.titleEN
                    this.newVideo.imageName = videoData.imageName
                    this.newVideo.thumbnailUrl = videoData.thumbnailUrl
                    this.newVideo.descriptionTA = videoData.descriptionTA
                    this.newVideo.descriptionEN = videoData.descriptionEN
                    this.newVideo.significantDate = videoData.significantDate
                    this.newVideo.publishState = videoData.publishState || 'pending'
                    this.newVideo.ownerId = videoData.ownerId || videoData.owner?.id || null
                    this.originalOwnerId = this.newVideo.ownerId
                    this.newVideo.videoModelPrimaryCategoryId = videoData.videoModelPrimaryCategoryId
                    this.newVideo.videoModelSecondaryCategoryId = videoData.videoModelSecondaryCategoryId
                    this.selectedOwner = videoData.owner || null
                    
                    // Find matching category objects from the categories array
                    if (videoData.primaryCategory) {
                        this.primaryCategory = this.categories.find(cat => cat.id === videoData.primaryCategory.id);
                    }
                    
                    if (videoData.secondaryCategory) {
                        // First find the parent category that contains this secondary category
                        const parentCategory = this.categories.find(cat => 
                            cat.children?.items?.some(child => child.id === videoData.secondaryCategory.id)
                        );
                        
                        if (parentCategory && parentCategory.children?.items) {
                            this.secondaryCategory = parentCategory.children.items.find(child => 
                                child.id === videoData.secondaryCategory.id
                            );
                        }
                    }
                    
                    // Load video tags (extract from VideoTags junction table)
                    this.videoTags = videoData.tags?.items?.map(videoTag => ({
                        ...videoTag.tagModel,
                        junctionId: videoTag.id // Store the junction record ID for deletion
                    })) || [];
                    
                    console.log('Video loaded successfully:', videoData);
                    console.log('Categories loaded:', this.categories.length);
                    console.log('Available tags loaded:', this.availableTags.length);
                    console.log('Video tags loaded:', this.videoTags.length);
                    console.log('Authorization check - isOwner:', this.isOwner, 'isAuthorized:', this.isAuthorized);
                }
            } catch (error) {
                console.error('Error loading video details:', error);
                this.videoNotFound = true;
            } finally {
                this.isLoading = false;
            }
        },
        updateVideo(video) {
            this.newVideo = { ...video };
        },
        updateTags(tags) {
            this.videoTags = [...tags];
        },
        updatePrimaryCategory(category) {
            this.primaryCategory = category;
        },
        updateSecondaryCategory(category) {
            this.secondaryCategory = category;
        },
        async searchOwners() {
            if (!this.canManageOwner) return;

            if (this.ownerSearchTimeout) {
                clearTimeout(this.ownerSearchTimeout);
            }

            const term = this.ownerSearchQuery.trim();
            if (!term || term.length < 2) {
                this.ownerSearchResults = [];
                this.ownerSearching = false;
                this.showOwnerResults = false;
                return;
            }

            this.ownerSearching = true;
            this.ownerSearchTimeout = setTimeout(async () => {
                try {
                    const searchTerm = this.ownerSearchQuery.trim();
                    const resultsById = new Map();

                    const addUsers = (users = []) => {
                        users
                            .filter(user => user?.id)
                            .filter(user => this.userMatchesOwnerSearch(user, searchTerm))
                            .forEach(user => resultsById.set(user.id, user));
                    };

                    await Promise.all(
                        this.usernameSearchVariants(searchTerm).map(async username => {
                            try {
                                const res = await API.graphql({
                                    query: getUserModelLiteByUsername,
                                    variables: { username },
                                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                                });
                                addUsers(res.data.userByUsername?.items || []);
                            } catch (error) {
                                console.warn('Owner username search failed:', username, error);
                            }
                        })
                    );

                    let nextToken = null;
                    for (let page = 0; page < 3; page += 1) {
                        const usersRes = await API.graphql({
                            query: listUserModelsForMessageSearch,
                            variables: { limit: 200, nextToken },
                            authMode: 'AMAZON_COGNITO_USER_POOLS'
                        });
                        const pageData = usersRes.data.listUserModels;
                        addUsers(pageData?.items || []);
                        nextToken = pageData?.nextToken || null;
                        if (!nextToken || resultsById.size >= 20) break;
                    }

                    if (this.ownerSearchQuery.trim() !== searchTerm) return;
                    this.ownerSearchResults = Array.from(resultsById.values()).slice(0, 20);
                    this.showOwnerResults = this.ownerSearchResults.length > 0;
                } catch (error) {
                    console.error('Error searching owners:', error);
                    this.ownerSearchResults = [];
                    this.showOwnerResults = false;
                } finally {
                    this.ownerSearching = false;
                }
            }, 300);
        },
        selectOwner(user) {
            if (!this.canManageOwner) return;

            this.selectedOwner = user;
            this.newVideo = {
                ...this.newVideo,
                ownerId: user.id
            };
            this.ownerSearchQuery = '';
            this.ownerSearchResults = [];
            this.showOwnerResults = false;
            analyticsTrack('video_owner_change_selected', {
                video_id: this.newVideo.id,
                new_owner_id: user.id
            });
        },
        usernameSearchVariants(term) {
            const compact = term.replace(/^@/, '').trim();
            const lower = compact.toLowerCase();
            const title = lower.charAt(0).toUpperCase() + lower.slice(1);
            return [...new Set([compact, lower, title])].filter(Boolean);
        },
        userMatchesOwnerSearch(user, term) {
            const needle = term.replace(/^@/, '').trim().toLowerCase();
            if (!needle) return false;
            return [
                user.username,
                user.displayName,
                user.firstName,
                user.lastName,
                `${user.firstName || ''} ${user.lastName || ''}`.trim()
            ].some(value => String(value || '').toLowerCase().includes(needle));
        },
        displayUserName(user) {
            if (!user) return this.ta ? 'அறியப்படாத பயனர்' : 'Unknown User';
            return user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || (this.ta ? 'அறியப்படாத பயனர்' : 'Unknown User');
        },
        getUserAvatar(user) {
            if (!user?.id) return '/avatar-placeholder.svg';
            if (user.legacyAvatar) {
                if (user.legacyAvatar.startsWith('public/')) {
                    return `https://cdn1.eelam.tv/${user.legacyAvatar}`;
                }
                return `https://cdn.eelam.tv/${user.legacyAvatar}`;
            }
            return `https://cdn1.eelam.tv/public/images/user/${user.id}/avatar.png`;
        },
        handleAvatarError(event) {
            if (event.target.src.endsWith('/avatar-placeholder.svg')) return;
            event.target.src = '/avatar-placeholder.svg';
        },
        async replaceVideoFile(file) {
            if (!file || !this.newVideo.id) return;

            if (!file.type.startsWith('video/')) {
                this.errorMessage = this.ta ? 'காணொளி கோப்பைத் தேர்ந்தெடுக்கவும்.' : 'Please choose a video file.';
                return;
            }

            this.errorMessage = '';
            this.replacementUploadStatus = '';
            this.replacementUploadMessage = '';
            this.replacementUploadProgress = 0;
            this.uploadingReplacement = true;
            this.preparingReplacement = false;
            this.replacementProcessingReadyHandled = false;

            try {
                await API.graphql({
                    query: updateVideoModel,
                    variables: {
                        input: {
                            id: this.newVideo.id,
                            status: 'processing',
                            size: file.size,
                            errorMessage: null
                        }
                    },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });

                analyticsTrack('video_replacement_upload_start', {
                    video_id: this.newVideo.id,
                    file_size: file.size,
                    file_type: file.type,
                    publish_state: this.newVideo.publishState
                });

                await Storage.put(`videos/raw/${this.newVideo.id}.mp4`, file, {
                    resumable: true,
                    contentType: file.type || 'video/mp4',
                    progressCallback: (progress) => {
                        if (progress.total) {
                            this.replacementUploadProgress = (progress.loaded / progress.total) * 100;
                        }
                    }
                });

                this.uploadingReplacement = false;
                this.preparingReplacement = true;
                this.replacementUploadProgress = 100;
                this.replacementUploadStatus = 'success';
                this.replacementUploadMessage = this.ta
                    ? 'பதிவேற்றம் முடிந்தது. காணொளி செயலாக்கப்படுகிறது.'
                    : 'Upload complete. The video is processing now.';
                this.newVideo.status = 'processing';
                analyticsTrack('video_replacement_upload_complete', {
                    video_id: this.newVideo.id,
                    file_size: file.size,
                    file_type: file.type
                });
                this.pollReplacementProcessing();
            } catch (error) {
                console.error('Error replacing video file:', error);
                analyticsTrack('video_replacement_upload_failed', {
                    video_id: this.newVideo?.id,
                    error_code: this.getAnalyticsErrorCode(error)
                });
                this.uploadingReplacement = false;
                this.preparingReplacement = false;
                this.replacementUploadStatus = 'error';
                this.replacementUploadMessage = '';
                this.errorMessage = this.getErrorMessage(error);
            }
        },
        pollReplacementProcessing() {
            if (this.replacementPollTimer) {
                clearInterval(this.replacementPollTimer);
            }

            const startTime = Date.now();
            const pollInterval = 5000;
            const pollTimeout = 10 * 60 * 1000;

            this.replacementPollTimer = setInterval(async () => {
                try {
                    const res = await API.graphql({
                        query: getVideoProcessingStatus,
                        variables: { id: this.newVideo.id },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    const video = res.data.getVideoModel;
                    const status = video?.status;

                    if (status === 'thumbsReady') {
                        this.thumbnailRefreshKey = Date.now();
                        this.replacementUploadMessage = this.ta
                            ? 'சிறுபடங்கள் தயாராகின்றன. இறுதி செயலாக்கம் தொடர்கிறது.'
                            : 'Thumbnails are ready. Final processing is continuing.';
                    }

                    if (status === 'processingReady' && !this.replacementProcessingReadyHandled) {
                        this.replacementProcessingReadyHandled = true;
                        this.preparingReplacement = false;
                        this.replacementUploadStatus = 'success';
                        this.replacementUploadMessage = this.ta
                            ? 'காணொளி பார்க்க தயாராக உள்ளது. உயர் தர பதிப்புகள் இன்னும் செயலாக்கப்படுகின்றன.'
                            : 'Replacement video is playable now. Higher quality versions are still processing.';
                        this.newVideo.status = status;
                        this.thumbnailRefreshKey = Date.now();
                        analyticsTrack('video_replacement_processing_ready', {
                            video_id: this.newVideo.id,
                            status,
                            elapsed_ms: Date.now() - startTime
                        });
                        await this.loadVideoDetails();
                    }

                    if (status === 'ready') {
                        this.preparingReplacement = false;
                        this.replacementUploadStatus = 'success';
                        this.replacementUploadMessage = this.ta
                            ? 'காணொளி முழுமையாக தயாராக உள்ளது.'
                            : 'Replacement video is fully ready.';
                        this.newVideo.status = status;
                        this.thumbnailRefreshKey = Date.now();
                        analyticsTrack('video_replacement_processing_ready', {
                            video_id: this.newVideo.id,
                            status,
                            elapsed_ms: Date.now() - startTime
                        });
                        await this.loadVideoDetails();
                        clearInterval(this.replacementPollTimer);
                        this.replacementPollTimer = null;
                    }

                    if (status === 'failed') {
                        this.preparingReplacement = false;
                        this.replacementUploadStatus = 'error';
                        this.errorMessage = video?.errorMessage || (this.ta ? 'காணொளி செயலாக்கம் தோல்வியடைந்தது.' : 'Video processing failed.');
                        analyticsTrack('video_replacement_processing_failed', {
                            video_id: this.newVideo.id,
                            error_code: video?.errorMessage ? 'processor_error' : 'unknown',
                            elapsed_ms: Date.now() - startTime
                        });
                        clearInterval(this.replacementPollTimer);
                        this.replacementPollTimer = null;
                    }
                } catch (err) {
                    console.error('Error polling replacement processing:', err);
                }

                if (Date.now() - startTime >= pollTimeout) {
                    this.preparingReplacement = false;
                    this.replacementUploadMessage = this.ta
                        ? 'பதிவேற்றம் முடிந்தது. செயலாக்க நிலையை பின்னர் சரிபார்க்கவும்.'
                        : 'Upload complete. Check back later for processing status.';
                    clearInterval(this.replacementPollTimer);
                    this.replacementPollTimer = null;
                    analyticsTrack('video_replacement_processing_timeout', {
                        video_id: this.newVideo.id,
                        elapsed_ms: Date.now() - startTime
                    });
                }
            }, pollInterval);
        },
        async saveVideo() {
            this.creating = true;
            this.errorMessage = ''; // Clear any previous errors
            
            try {
                // Get current user for owner field;
                
                const videoData = {
                    ...this.newVideo,
                    publishState: this.getSavePublishState(),
                    videoModelPrimaryCategoryId: this.primaryCategory?.id,
                    videoModelSecondaryCategoryId: this.secondaryCategory?.id
                };
                if (!this.canManageOwner) {
                    videoData.ownerId = this.originalOwnerId;
                }
                const ownerChanged = this.canManageOwner && this.originalOwnerId !== videoData.ownerId;

                analyticsTrack('video_publish_start', {
                    video_id: videoData.id,
                    mode: 'edit',
                    category_id: videoData.videoModelPrimaryCategoryId,
                    secondary_category_id: videoData.videoModelSecondaryCategoryId,
                    tag_count: this.videoTags.length,
                    publish_state: videoData.publishState,
                    owner_changed: ownerChanged
                });

                console.log('saving...', videoData);
                const saveResponse = await API.graphql({
                    query: updateVideoModel,
                    variables: { input: videoData },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                
                // Save video tags
                await this.saveVideoTags(videoData.id, this.videoTags);
                
                analyticsTrack('video_search_index_update', {
                    video_id: videoData.id,
                    action: 'upsert',
                    mode: 'edit',
                    success: true,
                    source: 'video_stream_lambda'
                });
                
                this.created = true;
                analyticsTrack('video_publish', {
                    video_id: videoData.id,
                    mode: 'edit',
                    category_id: videoData.videoModelPrimaryCategoryId,
                    secondary_category_id: videoData.videoModelSecondaryCategoryId,
                    tag_count: this.videoTags.length,
                    publish_state: videoData.publishState,
                    owner_changed: ownerChanged,
                    previous_owner_id: ownerChanged ? this.originalOwnerId : undefined,
                    new_owner_id: ownerChanged ? videoData.ownerId : undefined,
                    is_edit: true,
                });
                this.originalOwnerId = videoData.ownerId;
                console.log('Save response', saveResponse);
                this.clearPendingUploadReminder(videoData.id);

                // Redirect to watch page after 2 seconds
                setTimeout(() => {
                    this.$router.push(`/watch/${videoData.id}`);
                }, 1000);
            } catch (error) {
                console.error('Error saving video:', error);
                analyticsTrack('video_publish_failed', {
                    video_id: this.newVideo?.id,
                    mode: 'edit',
                    error_code: this.getAnalyticsErrorCode(error)
                });
                this.errorMessage = this.getErrorMessage(error);
            } finally {
                this.creating = false;
            }
        },
        async saveVideoTags(videoId, tags) {
            try {
                // First, get current tags to see what needs to be added/removed
                const currentVideoRes = await API.graphql({
                    query: getVideoModelWithTags,
                    variables: { id: videoId },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                
                const currentVideoTags = currentVideoRes.data.getVideoModel.tags?.items || [];
                const currentTagIds = currentVideoTags.map(videoTag => videoTag.tagModel.id);
                const currentJunctionIds = currentVideoTags.map(videoTag => videoTag.id);
                const newTagIds = tags.map(tag => tag.id).filter(id => !id.startsWith('temp-'));
                
                // Create new tags that don't exist yet
                const newTags = tags.filter(tag => tag.id.startsWith('temp-'));
                for (const tag of newTags) {
                    try {
                        const createTagRes = await API.graphql({
                            query: createTagModel,
                            variables: {
                                input: {
                                    label: tag.label,
                                    createdAt: new Date().toISOString(),
                                    updatedAt: new Date().toISOString()
                                }
                            },
                            authMode: 'AMAZON_COGNITO_USER_POOLS'
                        });
                        
                        // Add the new tag to newTagIds
                        newTagIds.push(createTagRes.data.createTagModel.id);
                    } catch (error) {
                        console.error('Error creating tag:', error);
                    }
                }
                
                // Remove tags that are no longer associated
                for (let i = 0; i < currentTagIds.length; i++) {
                    const currentTagId = currentTagIds[i];
                    const junctionId = currentJunctionIds[i];
                    
                    if (!newTagIds.includes(currentTagId)) {
                        try {
                            await API.graphql({
                                query: deleteVideoTags,
                                variables: {
                                    input: { id: junctionId } // Use junction record ID
                                },
                                authMode: 'AMAZON_COGNITO_USER_POOLS'
                            });
                            console.log('Removed tag association:', currentTagId);
                        } catch (error) {
                            console.error('Error removing tag association:', error);
                        }
                    }
                }
                
                // Add new tag associations
                for (const tagId of newTagIds) {
                    if (!currentTagIds.includes(tagId)) {
                        try {
                            await API.graphql({
                                query: createVideoTags,
                                variables: {
                                    input: {
                                        videoModelID: videoId,
                                        tagModelID: tagId
                                    }
                                },
                                authMode: 'AMAZON_COGNITO_USER_POOLS'
                            });
                            console.log('Added tag association:', tagId);
                        } catch (error) {
                            console.error('Error adding tag association:', error);
                        }
                    }
                }
            } catch (error) {
                console.error('Error saving video tags:', error);
            }
        },
        async deleteVideo() {
            this.deleted = false;
            this.deleting = true;
            this.errorMessage = ''; // Clear any previous errors
            analyticsTrack('video_delete_start', {
                video_id: this.newVideo?.id
            });

            try {
                console.log('deleting...', this.newVideo);
                const deleteResponse = await API.graphql({
                    query: deleteVideoModel,
                    variables: {
                        input: {
                            id: this.newVideo.id
                        }
                    },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                console.log('Delete response:', deleteResponse);
                
                analyticsTrack('video_search_index_update', {
                    video_id: this.newVideo.id,
                    action: 'delete',
                    mode: 'delete',
                    success: true,
                    source: 'video_stream_lambda'
                });
                
                this.deleted = true;
                analyticsTrack('video_delete', {
                    video_id: this.newVideo.id
                });
                this.clearPendingUploadReminder(this.newVideo.id);
                
                // Redirect to home page after 2 seconds
                setTimeout(() => {
                    this.$router.push('/');
                }, 2000);
            } catch (error) {
                console.error('Error deleting video:', error);
                analyticsTrack('video_delete_failed', {
                    video_id: this.newVideo?.id,
                    error_code: this.getAnalyticsErrorCode(error)
                });
                this.errorMessage = this.getErrorMessage(error);
            } finally {
                this.deleting = false;
            }
        },
        clearPendingUploadReminder(videoId) {
            const key = 'eelamPendingVideoUpload';
            const dismissedKey = 'eelamDismissedPendingVideoUploads';
            try {
                if (videoId) {
                    const dismissedIds = JSON.parse(localStorage.getItem(dismissedKey) || '[]');
                    const nextDismissedIds = Array.isArray(dismissedIds) ? dismissedIds : [];
                    if (!nextDismissedIds.includes(videoId)) {
                        nextDismissedIds.unshift(videoId);
                    }
                    localStorage.setItem(dismissedKey, JSON.stringify(nextDismissedIds.slice(0, 50)));
                }

                const pendingUpload = JSON.parse(localStorage.getItem(key) || 'null');
                if (!pendingUpload?.video?.id || pendingUpload.video.id === videoId) {
                    localStorage.removeItem(key);
                    window.dispatchEvent(new CustomEvent('eelam:pending-upload-cleared', { detail: { videoId } }));
                }
            } catch (error) {
                console.error('Error clearing pending upload reminder:', error);
                localStorage.removeItem(key);
                window.dispatchEvent(new CustomEvent('eelam:pending-upload-cleared', { detail: { videoId } }));
            }
        },
        isVerifiedCurrentUser() {
            const userModel = this.$parent?.currentUser?.model;
            return userModel?.status === 'verified';
        },
        canPublishImmediately() {
            return this.isAdmin || this.isVerifiedCurrentUser();
        },
        getSavePublishState() {
            return this.canPublishImmediately() ? 'published' : 'pending';
        },
        getAnalyticsErrorCode(error) {
            if (error?.code) return error.code;
            const message = String(error?.errors?.[0]?.message || error?.message || error?.name || '').toLowerCase();
            if (!message) return 'unknown';
            if (message.includes('not authorized') || message.includes('unauthorized')) return 'unauthorized';
            if (message.includes('network') || message.includes('fetch')) return 'network_error';
            if (message.includes('timeout') || message.includes('timed out')) return 'timeout';
            if (message.includes('validation')) return 'validation_error';
            if (message.includes('storage') || message.includes('s3')) return 'storage_error';
            return 'error';
        },
        
        getErrorMessage(error) {
            if (error.errors && error.errors.length > 0) {
                const graphqlError = error.errors[0];
                if (graphqlError.message) {
                    return graphqlError.message;
                }
            }
            
            if (error.message) {
                return error.message;
            }
            
            return 'An unexpected error occurred. Please try again.';
        }
    },
}
</script>

<style scoped>
.update-video-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 32px 20px 20px;
    scroll-margin-top: 80px;
}

.admin-owner-panel {
    background: var(--bg-card);
    border: 1px solid var(--border-light);
    border-radius: 12px;
    padding: 18px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px var(--shadow-color);
}

.admin-owner-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
}

.admin-owner-header h3 {
    margin: 0 0 4px;
    color: var(--text-secondary);
    font-size: 18px;
}

.admin-owner-header p {
    margin: 0;
    color: var(--text-tertiary);
    font-size: 13px;
}

.selected-owner {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 180px;
    justify-content: flex-end;
}

.selected-owner img,
.owner-result img {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
    background: var(--bg-tertiary);
}

.selected-owner div,
.owner-result span {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.selected-owner strong,
.owner-result strong {
    color: var(--text-secondary);
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.selected-owner span,
.owner-result small {
    color: var(--text-tertiary);
    font-size: 12px;
}

.owner-search-box {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 0 12px;
    background: var(--bg-input);
}

.owner-search-box input {
    flex: 1;
    height: 42px;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--text-secondary);
    font: inherit;
}

.owner-results {
    margin-top: 10px;
    border: 1px solid var(--border-light);
    border-radius: 8px;
    overflow: hidden;
    background: var(--bg-card);
}

.owner-result {
    width: 100%;
    border: 0;
    background: transparent;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    text-align: left;
    cursor: pointer;
    color: inherit;
}

.owner-result:hover {
    background: var(--bg-hover);
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    text-align: center;
    padding: 72px 20px 40px;
    scroll-margin-top: 80px;
}

.loading-spinner {
    margin-bottom: 20px;
    color: #ff1314;
}

.loading-container h3 {
    color: var(--text-secondary);
    margin-bottom: 10px;
    font-size: 24px;
}

.loading-container p {
    color: var(--text-tertiary);
    font-size: 16px;
    margin: 0;
}

.not-found {
    text-align: center;
    padding: 60px 20px;
    max-width: 500px;
    margin: 32px auto 0;
    background: var(--bg-card);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-color);
    scroll-margin-top: 80px;
}

.not-found h3 {
    color: var(--text-secondary);
    margin-bottom: 20px;
    font-size: 24px;
}

.not-found p {
    color: var(--text-tertiary);
    margin-bottom: 30px;
}

.unauthorized {
    text-align: center;
    padding: 60px 20px;
    max-width: 500px;
    margin: 32px auto 0;
    background: var(--bg-card);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-color);
    scroll-margin-top: 80px;
}

.unauthorized-icon {
    margin-bottom: 20px;
    color: #ff6b6b;
}

.unauthorized h3 {
    color: var(--text-secondary);
    margin-bottom: 15px;
    font-size: 24px;
}

.unauthorized p {
    color: var(--text-tertiary);
    margin-bottom: 30px;
    font-size: 16px;
    line-height: 1.5;
}

.unauthorized-actions {
    display: flex;
    gap: 15px;
    justify-content: center;
    flex-wrap: wrap;
}

.btn {
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    transition: all 0.2s;
}

.btn-primary {
    background: #ff1314;
    color: white;
}

.btn-primary:hover {
    background: #e00e0f;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 19, 20, 0.3);
}

.btn-secondary {
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    border: 1px solid var(--border-color);
}

.btn-secondary:hover {
    background: var(--bg-hover);
}
</style>
